import { useEffect, useState } from "react";
import { LiveKitRoom, RoomAudioRenderer, useParticipants, useTracks, VideoTrack } from "@livekit/components-react";
import { Track } from "livekit-client";
import { Eye, X } from "lucide-react";
import { ccApi, fmtTs } from "./ccApi";
import { Pill } from "./ccUi";

function Stage() {
  const tracks = useTracks([Track.Source.Camera, Track.Source.ScreenShare], { onlySubscribed: true });
  const participants = useParticipants();
  return (
    <div className="space-y-2">
      <div className="grid grid-cols-2 gap-1 bg-black rounded-md overflow-hidden min-h-[160px]" data-testid="cc-diag-stage">
        {tracks.length === 0 && <div className="col-span-2 text-xs text-zinc-500 flex items-center justify-center h-40">Waiting for video tracks…</div>}
        {tracks.map((t) => (
          <div key={t.publication?.trackSid || t.participant.identity} className="relative aspect-video bg-zinc-900">
            <VideoTrack trackRef={t} className="w-full h-full object-cover" />
            <span className="absolute bottom-1 left-1 text-[10px] bg-black/70 px-1.5 rounded text-white">{t.participant.name || t.participant.identity}</span>
          </div>
        ))}
      </div>
      <RoomAudioRenderer />
      <div className="text-xs text-zinc-400" data-testid="cc-diag-participants">
        {participants.length} visible participant(s): {participants.map((p) => p.identity).join(", ") || "—"}
      </div>
    </div>
  );
}

/* Part 7 — read-only diagnostics viewer. Token is hidden + subscribe-only
 * (no publish, no data channel); grant auto-expires server-side and is
 * revoked when this panel closes. */
export default function DiagnosticViewer({ grant, onClose }) {
  const [left, setLeft] = useState(0);
  useEffect(() => {
    const tick = () => setLeft(Math.max(0, Math.round((new Date(grant.grant.expires_at) - Date.now()) / 1000)));
    tick(); const t = setInterval(tick, 1000);
    return () => clearInterval(t);
  }, [grant]);
  useEffect(() => { if (left === 0 && grant) onClose(); }, [left, grant, onClose]);
  return (
    <div data-testid="cc-diag-viewer" className="border border-sky-500/40 bg-sky-500/5 rounded-md p-3 space-y-2">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-xs text-sky-200"><Eye size={14} /> Read-only diagnostics · <Pill tone="blue">hidden · no publish · no chat</Pill></div>
        <button type="button" data-testid="cc-diag-close" onClick={onClose} className="text-zinc-400 hover:text-white"><X size={16} /></button>
      </div>
      <div className="text-[11px] text-zinc-400 font-mono">grant {grant.grant.id.slice(0, 8)} · room {grant.grant.room} · expires {fmtTs(grant.grant.expires_at)} ({left}s)</div>
      <LiveKitRoom serverUrl={grant.url} token={grant.token} connect video={false} audio={false} options={{ adaptiveStream: true, dynacast: true }}>
        <Stage />
      </LiveKitRoom>
    </div>
  );
}

export async function openDiagnostics(streamId, reason) {
  return ccApi.diagnosticAccess(streamId, reason);
}
